import { useState, useEffect } from 'react';

import GraphTable from '../../../components/GraphTable';
import {
  Status as StatusInterface,
  GlanceParams,
  GraphTableData,
  DateRange,
  GlanceMarket,
  GlanceAdPlatform,
  IdNameObject
} from '../../../types';

import { endpoints, handleFetchEndpoint } from './../config';
import { fetchGlanceGridData, fetchSpgrGrid } from './../helpers';

interface Props {
  chosenMarketId: string;
  chosenAdPlatformId: string;
  chosenRetailerId: string;
  defaultMarket: GlanceMarket;
  defaultAdPlatform: GlanceAdPlatform;
  defaultRetailer: IdNameObject;
  dateRange: DateRange;
  defaultParams: any;
  currency: string;
}

const AmazonGrid = ({
  chosenMarketId,
  chosenAdPlatformId,
  defaultMarket,
  defaultAdPlatform,
  dateRange,
  defaultParams,
  currency,
  chosenRetailerId,
  defaultRetailer
}: Props): React.ReactElement => {
  const [tableData, setTableData] = useState<Array<any> | null>(null);
  const [spgrDataGrid, setSpgrDataGrid] = useState<Array<any> | null>(null);
  const [selectedTableData, setSelectedTableData] = useState<Array<GraphTableData> | null>(null);
  const [gridDataUnavailable, setGridDataUnavailable] = useState<boolean>(false);
  const [groupingOption, setGroupingOption] = useState<string | null>(null);
  const [gridIndex, setGridIndex] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<StatusInterface[]>([]);

  // const [timeRange, setTimeRange] = useState<string>(dateFilter);

  const glanceGridOptions = chosenRetailerId === '1' ? ['Sales', 'Media'] : ['Media'];

  const fetchGridData = (path: string | number, params: GlanceParams) => {
    let segments = endpoints[path].segments;
    if (chosenRetailerId !== '1') {
      segments = segments?.filter((segment) => segment?.id !== 'glanceGridSales');
    }

    segments?.forEach(({ endpoint, id, title }) => {
      (async () => {
        try {
          const data = await fetchGlanceGridData(endpoint, params, title);
          if (chosenRetailerId !== '1') {
            delete data.spgr;
          }
          setGridDataUnavailable(data ? false : true);
          setTableData((tableData) =>
            [...(tableData || []), ...handleFetchEndpoint[path].grid(id, data)].sort((a, b) => a.index - b.index)
          );
        } catch (error: any) {
          setStatus([...status, { state: 'error', message: error }]);
        }
      })();
    });
  };

  const fetchSpgrGridData = async (params: any) => {
    try {
      const spgrData = await fetchSpgrGrid(params);
      setSpgrDataGrid(spgrData.spgr);
    } catch (error: any) {
      setStatus([...status, { state: 'error', message: error }]);
    }
  };

  // fetch data
  useEffect(() => {
    if (defaultParams) {
      //reset grid data
      resetTableData();
      setLoading(true);

      const params = {
        ...defaultParams,
        marketIds: chosenMarketId === defaultMarket.id ? '' : chosenMarketId,
        adPlatformIds: chosenAdPlatformId === defaultAdPlatform.id ? '' : chosenAdPlatformId,
        retailerIds: chosenRetailerId === defaultRetailer.id ? '' : chosenRetailerId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      };
      const path = 'grid';

      fetchGridData(path, params);
      fetchSpgrGridData(params);
    }
  }, [chosenMarketId, defaultParams, chosenRetailerId]);

  const updateTable = () => {
    if (tableData && tableData[gridIndex]) {
      setSelectedTableData(tableData[gridIndex].data);
    } else {
      setSelectedTableData(null);
      setGridDataUnavailable(true);
    }
  };

  useEffect(() => {
    let segments = endpoints['grid'].segments;
    if (chosenRetailerId !== '1') {
      segments = segments?.filter((segment) => segment?.id !== 'glanceGridSales');
    }

    if (tableData) {
      if (tableData?.length === segments?.length) {
        setLoading(false);
        updateTable();
      }
    } else {
      updateTable();
    }
  }, [tableData]);

  useEffect(() => {
    updateTable();
  }, [gridIndex]);

  useEffect(() => {
    if (groupingOption) {
      setGridIndex(glanceGridOptions.indexOf(groupingOption));
    }
  }, [groupingOption, glanceGridOptions]);

  const resetTableData = () => {
    setGridDataUnavailable(false);
    setTableData(null);
    setSelectedTableData(null);
    setSpgrDataGrid(null);
  };

  if (tableData && tableData[1] && tableData[1].data && spgrDataGrid && tableData[1].data.length < 4) {
    const newTableData = tableData;
    newTableData[1].data.push(spgrDataGrid);
    setTableData(newTableData);
  }

  return (
    <div className="border-t border-borderGray relative">
      <GraphTable
        options={glanceGridOptions}
        selectedCurrency={currency}
        data={selectedTableData}
        staticTime={true}
        timeRange={undefined}
        onOptionChange={setGroupingOption}
        unavailableData={gridDataUnavailable}
        loading={loading}
        showTooltips={chosenMarketId === defaultMarket.id ? true : false}
      />
    </div>
  );
};

export default AmazonGrid;
